import { Node, Edge } from '@xyflow/react';
import { CheckpointConfig, PipelineOutputFormat } from '../../../../api/structs/pipeline';

// ==================== Node ids ====================
// Singleton nodes use fixed ids (mirrors the generators-canvas convention of
// BASE_MODEL_NODE_ID etc.); checkpoint nodes are prefixed with the checkpoint id.

export const PARAMS_NODE_ID = 'params';
export const INPUT_NODE_ID = 'input';
export const ATTACHMENTS_NODE_ID = 'attachments';
export const SETTINGS_NODE_ID = 'settings';
export const CHECKPOINT_NODE_PREFIX = 'cp:';

export const checkpointNodeId = (checkpointId: string): string =>
  `${CHECKPOINT_NODE_PREFIX}${checkpointId}`;
export const isCheckpointNodeId = (nodeId: string): boolean =>
  nodeId.startsWith(CHECKPOINT_NODE_PREFIX);
export const checkpointIdFromNodeId = (nodeId: string): string =>
  isCheckpointNodeId(nodeId) ? nodeId.slice(CHECKPOINT_NODE_PREFIX.length) : '';

// ==================== Handle ids ====================
// Checkpoint sources: `out` always, plus `out:<field>` per field any consumer
// selects via `checkpoint:<id>.<field>`. Checkpoint targets: `in:<key>` per
// input_mapping key, `asset:<key>` per required_assets entry, and config pins.

export const OUT_HANDLE = 'out';
export const OUT_FIELD_PREFIX = 'out:';
export const outFieldHandle = (field: string): string => `${OUT_FIELD_PREFIX}${field}`;
export const IN_HANDLE_PREFIX = 'in:';
export const inHandle = (key: string): string => `${IN_HANDLE_PREFIX}${key}`;
// Structural (derived-edge) target handles live in their own `sys:` namespace:
// `in:<key>` is RESERVED for input_mapping keys, so the later inverse mutation
// can parse `in:<key>` to recover the mapping key without a user-named key
// ("collect"/"chain") colliding with a structural pin.
export const SYS_HANDLE_PREFIX = 'sys:';
/** Connectors collect without an input_mapping — their derived edge lands here. */
export const COLLECT_HANDLE = 'sys:collect';
/** Chain-connection derived edges (resolvable chain_connections entries) land here. */
export const CHAIN_HANDLE = 'sys:chain';
export const ASSET_HANDLE_PREFIX = 'asset:';
export const assetHandle = (key: string): string => `${ASSET_HANDLE_PREFIX}${key}`;
export const CFG_MAX_CHILDREN_HANDLE = 'cfg:max_children_param';
/** A sequencer's step cap, wired from a run parameter. */
export const CFG_MAX_STEPS_HANDLE = 'cfg:max_steps_param';
export const CFG_SKIP_WHEN_HANDLE = 'cfg:skip_when';
/** A sequencer's named parent references (`allowed_parent_refs`). */
export const REF_HANDLE_PREFIX = 'ref:';
export const refHandle = (name: string): string => `${REF_HANDLE_PREFIX}${name}`;
export const PARAM_HANDLE_PREFIX = 'param:';
export const paramHandle = (name: string): string => `${PARAM_HANDLE_PREFIX}${name}`;
/** The input node's single source handle. */
export const INITIAL_HANDLE = 'initial';
export const ATT_ALL_HANDLE = 'att:all';
export const ATT_HANDLE_PREFIX = 'att:';
export const attHandle = (checkpointId: string): string => `${ATT_HANDLE_PREFIX}${checkpointId}`;

// ---- Handle parsing (inverse direction, used by graphMutations) ----
// Each returns the suffix after its prefix, or '' when the handle is not of
// that shape (an exact-prefix handle like `in:` also yields '' — callers treat
// empty as invalid). Keeping every prefix in this file means a scheme change
// stays single-file.
const suffixAfter = (handle: string, prefix: string): string =>
  handle.startsWith(prefix) ? handle.slice(prefix.length) : '';
export const isSysHandle = (handle: string): boolean => handle.startsWith(SYS_HANDLE_PREFIX);
export const mappingKeyFromHandle = (handle: string): string => suffixAfter(handle, IN_HANDLE_PREFIX);
export const assetKeyFromHandle = (handle: string): string => suffixAfter(handle, ASSET_HANDLE_PREFIX);
export const outFieldFromHandle = (handle: string): string => suffixAfter(handle, OUT_FIELD_PREFIX);
export const paramNameFromHandle = (handle: string): string => suffixAfter(handle, PARAM_HANDLE_PREFIX);
export const attachmentIdFromHandle = (handle: string): string => suffixAfter(handle, ATT_HANDLE_PREFIX);
export const parentRefNameFromHandle = (handle: string): string => suffixAfter(handle, REF_HANDLE_PREFIX);

// ==================== Edge ids ====================
// Deterministic — the same template always yields the same ids, so React Flow
// state (selection, hover) survives a re-derivation.

export const edgeId = (
  sourceNode: string,
  sourceHandle: string,
  targetNode: string,
  targetHandle: string
): string => `e|${sourceNode}|${sourceHandle}|${targetNode}|${targetHandle}`;

// ==================== Node data ====================

export interface CheckpointNodeData {
  kind: 'checkpoint';
  /**
   * A detached deep clone of the template's CheckpointConfig (plain-JSON
   * clone — checkpoint configs come off the wire as JSON). Cloning keeps the
   * derivation pure at its boundary: later mutations that write through node
   * data can never reach back into the caller's template object, which would
   * break immutable React state updates.
   */
  checkpoint: CheckpointConfig;
  /** `out` first, then `out:<field>` in first-referenced order. */
  sourceHandles: string[];
  /** `in:<key>` in input_mapping key order, then sys:collect/sys:chain, asset:<key>, cfg pins. Unique. */
  targetHandles: string[];
  /**
   * Input-mapping keys the UI should offer via the `in:+` affordance but which
   * are NOT mapped (and so not handles) yet: prompt-template `{{placeholders}}`
   * ∪ type-specific known keys, minus the existing input_mapping keys.
   */
  suggestedKeys: string[];
  /**
   * Mappings that produce no edge (literal:/advanced raw values), keyed by
   * input_mapping key with the raw stored string — the UI renders a badge.
   */
  inlineValues: Record<string, string>;
  [key: string]: unknown;
}

export interface ParamsNodeEntry {
  name: string;
  /** False for params referenced somewhere but missing from template.parameters. */
  declared: boolean;
  default?: string;
}

export interface ParamsNodeData {
  kind: 'params';
  /** Declared params in declaration order, then undeclared in first-referenced order. */
  params: ParamsNodeEntry[];
  [key: string]: unknown;
}

export interface InputNodeData {
  kind: 'input';
  [key: string]: unknown;
}

export interface AttachmentsNodeData {
  kind: 'attachments';
  /** Distinct `attachments:<id>` targets in first-referenced order. */
  referencedCheckpointIds: string[];
  /** `att:all` first, then `att:<id>` per referenced checkpoint id. */
  sourceHandles: string[];
  [key: string]: unknown;
}

export interface SettingsNodeData {
  kind: 'settings';
  outputFormat?: PipelineOutputFormat;
  [key: string]: unknown;
}

export type PipelineCanvasNodeData =
  | CheckpointNodeData
  | ParamsNodeData
  | InputNodeData
  | AttachmentsNodeData
  | SettingsNodeData;

export type PipelineCanvasNode = Node<PipelineCanvasNodeData>;

// ==================== Edge data ====================

/**
 * `review_*` kinds belong to the READ-ONLY reviewer graph (`../../reviewer/`),
 * which reuses this canvas' node and edge rendering: three for what flows INTO
 * the judge (words / pictures / criteria) and three for the fork out of its
 * verdict.
 */
export type PipelineCanvasEdgeKind =
  | 'mapping'
  | 'asset'
  | 'param'
  | 'cfg'
  | 'derived'
  | 'review_text'
  | 'review_image'
  | 'review_check'
  | 'review_pass'
  | 'review_uncertain'
  | 'review_fail';

export interface PipelineCanvasEdgeData {
  kind: PipelineCanvasEdgeKind;
  /** The raw stored string the edge was derived from (input_mapping value, param name selector, …). */
  dsl: string;
  /** The input_mapping key, for mapping/param edges. */
  mappingKey?: string;
  /** The required_assets key, for asset edges. */
  assetKey?: string;
  /** True for read-only structural edges (connector collect, chain connections). */
  derived?: boolean;
  [key: string]: unknown;
}

export type PipelineCanvasEdge = Edge<PipelineCanvasEdgeData>;

// ==================== Result ====================

export interface DanglingRef {
  /** The CONSUMING checkpoint whose config points at something unresolvable. */
  checkpointId: string;
  /** 'duplicate': an edge whose deterministic id collided with an earlier one
   * and was collapsed — recorded so nothing disappears silently. */
  kind: 'mapping' | 'asset' | 'connector' | 'duplicate';
  detail: string;
}

export interface PipelineCanvasGraph {
  nodes: PipelineCanvasNode[];
  edges: PipelineCanvasEdge[];
  danglingRefs: DanglingRef[];
}
